export const BOX_TYPES = ['highlight', 'rect', 'ellipse', 'textbox']
export const LINE_TYPES = ['line', 'arrow']

export function clamp01(n) {
  return Math.min(1, Math.max(0, n))
}

export function eventToNorm(e, el) {
  const r = el.getBoundingClientRect()
  if (!r.width || !r.height) return { x: 0, y: 0 }
  return {
    x: clamp01((e.clientX - r.left) / r.width),
    y: clamp01((e.clientY - r.top) / r.height),
  }
}

export function bboxFrom(a, b) {
  const x = Math.min(a.x, b.x)
  const y = Math.min(a.y, b.y)
  return { x, y, w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) }
}

export function dist(a, b) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}

export function isBoxType(type) {
  return BOX_TYPES.includes(type)
}

export function inkBounds(points) {
  if (!points?.length) return { x: 0, y: 0, w: 0, h: 0 }
  let minX = 1
  let minY = 1
  let maxX = 0
  let maxY = 0
  for (const p of points) {
    minX = Math.min(minX, p.x)
    minY = Math.min(minY, p.y)
    maxX = Math.max(maxX, p.x)
    maxY = Math.max(maxY, p.y)
  }
  return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) }
}

export function boundsOf(markup) {
  const g = markup.geometry || {}
  if (isBoxType(markup.type)) return { x: g.x || 0, y: g.y || 0, w: g.w || 0, h: g.h || 0 }
  if (LINE_TYPES.includes(markup.type)) {
    return bboxFrom({ x: g.x1 || 0, y: g.y1 || 0 }, { x: g.x2 || 0, y: g.y2 || 0 })
  }
  if (markup.type === 'ink') return inkBounds(g.points)
  return { x: 0, y: 0, w: 0, h: 0 }
}

export function translateGeometry(type, geometry, dx, dy) {
  if (isBoxType(type)) {
    return { ...geometry, x: geometry.x + dx, y: geometry.y + dy }
  }
  if (LINE_TYPES.includes(type)) {
    return {
      x1: geometry.x1 + dx,
      y1: geometry.y1 + dy,
      x2: geometry.x2 + dx,
      y2: geometry.y2 + dy,
    }
  }
  if (type === 'ink') {
    return { points: (geometry.points || []).map((p) => ({ x: p.x + dx, y: p.y + dy })) }
  }
  return geometry
}

export function resizeBox(geometry, handle, point) {
  const x2 = geometry.x + geometry.w
  const y2 = geometry.y + geometry.h
  let nx = geometry.x
  let ny = geometry.y
  let nx2 = x2
  let ny2 = y2
  if (handle.includes('w')) nx = point.x
  if (handle.includes('e')) nx2 = point.x
  if (handle.includes('n')) ny = point.y
  if (handle.includes('s')) ny2 = point.y
  return bboxFrom({ x: nx, y: ny }, { x: nx2, y: ny2 })
}

export function resizeLine(geometry, handle, point) {
  if (handle === 'start') return { ...geometry, x1: point.x, y1: point.y }
  return { ...geometry, x2: point.x, y2: point.y }
}

export function defaultStyle(type, color) {
  if (type === 'highlight') return { color, opacity: 0.4, strokeWidth: 0 }
  if (type === 'textbox') return { color, opacity: 1, strokeWidth: 1, fontSize: 14 }
  return { color, opacity: 1, strokeWidth: 2 }
}

export function geometryFromDraw(type, start, current, points) {
  if (type === 'ink') return { points }
  if (type === 'line' || type === 'arrow') {
    return { x1: start.x, y1: start.y, x2: current.x, y2: current.y }
  }
  return bboxFrom(start, current)
}

export function isDrawable(type, geometry) {
  if (type === 'ink') return (geometry.points || []).length >= 2
  if (type === 'line' || type === 'arrow') {
    return dist({ x: geometry.x1, y: geometry.y1 }, { x: geometry.x2, y: geometry.y2 }) > 0.01
  }
  if (type === 'textbox') return true
  return (geometry.w || 0) > 0.008 && (geometry.h || 0) > 0.008
}

export function clampBoxToPage(geometry) {
  const w = Math.min(geometry.w, 1)
  const h = Math.min(geometry.h, 1)
  return {
    x: clamp01(geometry.x),
    y: clamp01(geometry.y),
    w,
    h,
  }
}
